/**
 * Text Filter Categories Configuration
 * Comprehensive bog_score settings with language-specific thresholds and word lists
 */

export interface LanguageThresholds {
  language: string;
  thresholds: {
    0: number; // Maximum threshold (usually 5000 = effectively disabled)
    1: number; // High sensitivity
    2: number; // Medium sensitivity
    3: number; // Low sensitivity
  };
  words: string[];
}

export interface TextFilterCategory {
  name: string;
  displayName: string;
  default: number; // Default threshold level (1, 2, or 3)
  alwaysOn: boolean;
  languages: LanguageThresholds[];
}

export const TEXT_FILTER_CATEGORIES: TextFilterCategory[] = [
  {
    name: "Adult",
    displayName: "Adult Content",
    default: 3,
    alwaysOn: true,
    languages: [
      {
        language: "En",
        thresholds: { 0: 5000, 1: 1, 2: 1, 3: 1 },
        words: [
          "$_nude_model", "$_nude_photo", "$_nude_picture", "$_nude_photography", "$_nude_image",
          "$_nude_painting", "$_nude_sculpture", "$_nude_act", "$_nude_performance", "$_nude_dance",
          "$_nude_show", "$_nude_exhibition", "$_nude_pose", "$_nude_style", "$_nude_fashion",
          "$_porn_sex", "$_porn_star", "$_porn_film", "$_porn_movie", "$_porn_nude",
          "$_erotic_audio_book", "$_erotic_romance", "$_erotic_art", "$_adult_animation",
          "$_adult_photoshoot", "$_barely_covered", "$_boudoir_photography", "$_nude_beach",
          "$_nude_calendar", "$_nude_leak", "$_nude_magazine", "$_nude_resort", "$_nude_scene",
          "$_nude_selfie", "$_nude_yoga", "$_provocative_pose", "$_revealing_outfit"
        ]
      },
      {
        language: "En",
        thresholds: { 0: 5000, 1: 3, 2: 3, 3: 2 },
        words: [
          "porn", "porno", "pronography", "pornography", "pr0n", "erotica", "xxx",
          "explicit_material", "erotic_content", "sexually_explicit", "sexual", "nsfw",
          "naked", "nudity", "fetish", "kinky", "sexy_underwear", "intimate_apparel",
          "adult_toys", "ahegao", "bdsm", "bondage", "busty", "cam_girl", "camgirl",
          "dirty_talk", "domination", "horny", "kink", "lewd", "naughty", "nudes",
          "pornhub", "pornstar", "risque", "seductive", "sexting", "smut", "uncensored"
        ]
      },
      {
        language: "En",
        thresholds: { 0: 5000, 1: 6, 2: 5, 3: 4 },
        words: [
          "adult_content", "age_restricted", "mature_audiences", "parental_advisory",
          "explicit_lyrics", "graphic_content", "mature_themes", "restricted_viewing",
          "adult_entertainment", "sensitive_material", "explicit_scene", "age_limit",
          "gentlemens_club", "adult_only", "mature_rating", "parental_guidance",
          "adult_humor", "rated_r", "explicit_content", "sensitive_content",
          "onlyfans", "only_fans", "adult_influencer", "18_plus", "21_plus",
          "adult_creator", "adult_platform", "adult_website", "mature_content"
        ]
      },
      {
        language: "Es",
        thresholds: { 0: 5000, 1: 2, 2: 2, 3: 1 },
        words: [
          "$_modelo_desnuda", "$_foto_desnuda", "$_fotografia_desnuda", "$_arte_desnudo",
          "$_desnudo_artistico", "$_desnudo_romantico", "$_placer_adulto", "$_fantasia_erotica",
          "$_industria_adultos", "$_cine_adulto", "$_calendario_desnudo", "$_revista_adultos",
          "$_playa_nudista", "$_resort_nudista", "$_yoga_desnudo", "$_fotografia_boudoir"
        ]
      },
      {
        language: "Es",
        thresholds: { 0: 5000, 1: 3, 2: 3, 3: 2 },
        words: [
          "porno", "pornografia", "xxx", "material_explicito", "contenido_erotico",
          "sexual", "nsfw", "desnudo", "nudez", "fetiche", "lenceria_erotica",
          "lujuria", "deseo_sexual", "orgasmo", "masturbacion", "escort"
        ]
      },
      {
        language: "He",
        thresholds: { 0: 5000, 1: 4, 2: 3, 3: 2 },
        words: [
          "$_אמנות_ארוטית", "$_אמנות_עירום", "$_גלריית_עירום", "$_פוזה_מרומזת",
          "$_פיסול_עירום", "$_ציור_עירום", "$_צילום_בודואר", "$_צילום_עירום"
        ]
      }
    ]
  },
  {
    name: "AdultsVeryStrict",
    displayName: "Adult (Very Strict)",
    default: 1,
    alwaysOn: true,
    languages: [
      {
        language: "En",
        thresholds: { 0: 5000, 1: 100, 2: 3, 3: 1 },
        words: [
          "porno", "pr0n", "erotica", "porn", "nsfw", "xxx", "fuck", "fucking", "bdsm",
          "creampie", "blowjob", "hard_cock", "fetish", "kinky", "orgasm", "sex",
          "bukkake", "cuckold", "hentai", "lesbian", "gay", "orgy", "pussy", "vagina",
          "gangbang", "bigass", "bigboobs", "topless", "tits", "milf", "dildo",
          "chaturbate", "onlyfans", "clitoris", "twerk", "striptease", "horny",
          "pornhub", "xvideos", "xhamster", "redtube", "brazzers", "escort",
          "$_bikini_party", "$_girls_gone_wild", "$_wet_tshirt", "$_wild_party_girls",
          "$_kissing_girls", "$_lesbian_kissing", "$_girl_on_girl", "$_girls_kissing_girls"
        ]
      },
      {
        language: "Es",
        thresholds: { 0: 5000, 1: 5, 2: 4, 3: 3 },
        words: [
          "$_baile_tubo", "$_club_caballeros", "$_contenido_adulto", "$_desnudo_integral",
          "18+", "actor_porno", "actriz_porno", "bdsm", "bisexual", "bondage",
          "camgirl", "chaturbate", "contenido_explicito", "desnuda", "dildo",
          "escort", "fetiche", "follar", "lesbico", "masturbacion", "milf",
          "onlyfans", "orgasmo", "orgia", "porno", "pornografia", "prostituta",
          "sexo", "stripper", "tetas", "topless", "vagina", "xxx"
        ]
      },
      {
        language: "He",
        thresholds: { 0: 5000, 1: 5, 2: 3, 3: 2 },
        words: [
          "18+", "chaturbate", "onlyfans", "pornhub", "stripchat", "xvideos", "xxx",
          "אוננות", "אורגזמה", "אירוטי", "פורנו", "פורנוגרפיה", "סקס", "עירום_מלא",
          "זונה", "חשפנות", "מין_אוראלי", "סרט_פורנו", "תוכן_מיני"
        ]
      }
    ]
  },
  {
    name: "Gambling",
    displayName: "Gambling",
    default: 2,
    alwaysOn: false,
    languages: [
      {
        language: "En",
        thresholds: { 0: 5000, 1: 5, 2: 4, 3: 3 },
        words: [
          "slot_machine", "roulette_wheel", "blackjack_table", "craps_table", "baccarat_table",
          "casino_chip", "croupier", "house_edge", "video_poker_machine", "high_roller_suite",
          "double_down", "casino_resort", "jackpot_winner", "card_counting", "keno_lounge",
          "casino_floor", "sports_betting", "point_spread", "over_under", "bookmaker_odds",
          "parlay_bet", "horse_racing_wager", "betting_slip", "accumulator_bet", "fixed_odds",
          "in_play_betting", "betting_exchange", "betting_strategy", "prop_bet", "futures_bet",
          "gambling_addiction", "lottery_ticket", "scratch_card", "betting_limit", "betting_shop",
          "online_gambling", "live_casino", "$_online_slots", "$_casino_online", "$_virtual_casino",
          "$_esports_bet", "$_crypto_casino", "gambling_site", "betting_app", "bitcoin_gambling",
          "$_betting_bonus", "$_betting_odds", "$_deposit_bonus", "$_free_bet", "$_live_odds",
          "$_poker_tournament", "$_progressive_jackpot", "$_slot_game", "$_welcome_bonus",
          "bet", "cashout", "casino", "chips", "dice", "gamble", "mega_moolah", "odds",
          "poker", "spin", "sportsbook", "stake", "wager"
        ]
      },
      {
        language: "Es",
        thresholds: { 0: 5000, 1: 5, 2: 4, 3: 3 },
        words: [
          "máquina_tragaperras", "ruleta_casino", "mesa_blackjack", "ficha_casino", "crupier",
          "torneo_póker", "ganador_bote", "contar_cartas", "apuestas_deportivas", "apuesta_parlay",
          "apuesta_carreras", "boleta_apuesta", "estrategia_apuestas", "adicción_juego",
          "boleto_lotería", "tarjeta_rasca", "casa_apuestas", "apuesta", "bono_bienvenida",
          "casino_en_linea", "tragaperras", "ruleta_en_linea", "blackjack_en_linea",
          "apuestas_deportivas_en_linea", "casino_movil", "casino_bitcoin"
        ]
      },
      {
        language: "He",
        thresholds: { 0: 5000, 1: 5, 2: 4, 3: 3 },
        words: []
      }
    ]
  },
  {
    name: "Celebrities",
    displayName: "Celebrities & Gossip",
    default: 2,
    alwaysOn: false,
    languages: [
      {
        language: "En",
        thresholds: { 0: 5000, 1: 7, 2: 4, 3: 3 },
        words: [
          "$_red_carpet", "celebrity_feud", "grammys", "celeb_wedding", "paparazzi",
          "star_makeover", "celeb_rumors", "star_romance", "celebrity_rehab", "gossip",
          "celeb_memoir", "hollywood_elite", "celeb_scandal", "star_studded", "famous_breakup",
          "celebrity_party", "hollywood_debut", "celeb_fashion", "$_film_premiere",
          "celebrity_award", "$_Cannes_Film_Festival", "hollywood_agent", "celeb_interview",
          "gossip_magazine", "celebrity_trend", "celebrity", "breakup_details", "baby_bump",
          "$_sudden_split", "hollywood_affair", "secret_relationship", "heartfelt_message",
          "$_shocking_revelation", "celebrity_update", "$_reality_TV", "celebrity_lifestyle",
          "dating_rumors", "leaked_photos", "on-set_romance", "backstage_moments",
          "showbiz", "entertainment_news", "fan_following", "a-lister", "tabloid",
          "$_cheating_rumors", "entertainment_industry", "celebrity_gossip", "celebrity_news",
          "$_TikTok_celebrity", "$_instagram_influencer", "$_music_award_show",
          "$_reality_TV_star", "$_fashion_influencer", "$_entertainment_scandals",
          "$_brand_ambassador", "$_movie_premiere", "$_walk_of_fame", "emmys",
          "golden_globes", "magazine_cover", "met_gala", "oscar", "people_magazine",
          "spotify_wrapped", "tmz", "vanity_fair", "viral_moment", "vogue"
        ]
      },
      {
        language: "Es",
        thresholds: { 0: 5000, 1: 7, 2: 5, 3: 4 },
        words: [
          "famoso", "alfombra_roja", "hollywood", "celebridades", "estrella_pop",
          "actor_reconocido", "actriz_destacada", "rumor_romance", "chisme", "prensa_rosa",
          "noviazgo", "romance_publico", "escándalo", "farandula", "gira_musical",
          "premios_tv", "divorcio_sorpresivo", "paparazzi", "affaire", "telenovela",
          "fiesta_glamour", "drama_familiar", "fotos_exclusivas", "entrevista_intima",
          "$_entrevista_exclusiva", "$_ruptura_amorosa", "premios_oscar", "revista_hola"
        ]
      },
      {
        language: "He",
        thresholds: { 0: 5000, 1: 5, 2: 4, 3: 3 },
        words: []
      }
    ]
  },
  {
    name: "Swimsuite",
    displayName: "Swimwear Content",
    default: 2,
    alwaysOn: false,
    languages: [
      {
        language: "En",
        thresholds: { 0: 5000, 1: 5, 2: 4, 3: 3 },
        words: [
          "bikini_top", "one-piece_swimsuit", "beach_cover-up", "swimwear_collection",
          "tankini", "monokini", "swimsuit_issue", "swim_lookbook", "high-waisted_bikini",
          "string_bikini", "swimsuit_fashion", "bathing_suit", "swimwear_brand",
          "swim_photoshoot", "rash_guard", "swimsuit_model", "beachwear", "swim_suit",
          "swimsuit_designer", "swim_briefs", "swimsuit_runway", "beach_ready",
          "swim_collection", "$_beach_body", "$_bikini_haul", "$_pool_party",
          "$_sports_illustrated_swimsuit", "$_swimsuit_edition", "$_swimsuit_try_on",
          "$_swimwear_brand", "bikini_model", "resort_wear", "swimwear_influencer"
        ]
      },
      {
        language: "He",
        thresholds: { 0: 5000, 1: 5, 2: 4, 3: 3 },
        words: []
      }
    ]
  },
  {
    name: "Dating",
    displayName: "Dating & Romance",
    default: 3,
    alwaysOn: false,
    languages: [
      {
        language: "En",
        thresholds: { 0: 5000, 1: 5, 2: 4, 3: 3 },
        words: [
          "dating_profile", "blind_date", "relationship_status", "dating_advice",
          "matchmaking_service", "date_night_outfit", "dating_site", "romantic_interest",
          "date_idea", "courtship", "relationship_advice", "date_night", "speed_dating",
          "$_dating_tips", "online_dating", "setup_date", "dating_etiquette",
          "relationship_goals", "partner_preferences", "date_planning", "dating_strategy",
          "finding_love_online", "dating_milestone", "romantic_connection",
          "dating_scene", "mutual_match", "match_percentage", "love_algorithm",
          "dating_app_match", "$_compatibility_score", "$_virtual_date",
          "flirt_wink", "$_boosted_profile", "romantic_intention", "mutual_attraction",
          "flirtatious_exchange", "intimate_moment", "dating_expectations",
          "love_language", "relationship_journey", "emotional_intimacy",
          "$_dating_coach", "$_relationship_guru", "$_singles_event",
          "bumble", "casual_dating", "dating_app", "eharmony", "hinge",
          "hookup", "match", "okcupid", "pof", "serious_relationship", "tinder"
        ]
      },
      {
        language: "He",
        thresholds: { 0: 5000, 1: 5, 2: 4, 3: 3 },
        words: []
      }
    ]
  }
];

// Get categories grouped by always-on status
export const getAlwaysOnCategories = () => 
  TEXT_FILTER_CATEGORIES.filter(c => c.alwaysOn);

export const getOptionalCategories = () => 
  TEXT_FILTER_CATEGORIES.filter(c => !c.alwaysOn);

// Get threshold value for a specific level and category
export const getThresholdForLevel = (
  category: TextFilterCategory, 
  language: string, 
  level: 1 | 2 | 3
): number => {
  const langConfig = category.languages.find(l => l.language === language);
  if (!langConfig) return category.default;
  return langConfig.thresholds[level];
};

// Map NSFW preset level to threshold level
export const presetToThresholdLevel = (preset: "light" | "medium" | "strict"): 1 | 2 | 3 => {
  switch (preset) {
    case "light": return 3;    // Least sensitive
    case "medium": return 2;   // Medium sensitivity
    case "strict": return 1;   // Most sensitive
  }
};
